import CallRoundedIcon from "@mui/icons-material/CallRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import { Box, Button, Link, Typography } from "@mui/material";
import {
  PRESENCE_STATUS_LABELS,
  ROLE_LABELS,
  type PresenceStatus,
} from "@xop/dashboard-shared";
import type { JSX } from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useSessionUser } from "../auth/AuthProvider.js";
import { useCanSeeSection } from "../auth/permissions.js";
import { useCallController } from "../calls/CallProvider.js";
import { useCallsEnabled } from "../calls/useCallsEnabled.js";
import { useRealtime } from "../lib/notifications/RealtimeProvider.js";
import { useUserProfile } from "../lib/users/useUserProfile.js";
import { PresenceDot, type PresenceState } from "./PresenceDot.js";
import { UserAvatar } from "./UserAvatar.js";

/**
 * The profile-card body shown in the click-to-view popover. Given a teammate's
 * `userId` + a `name` to render instantly, it enriches with the cached
 * `useUserProfile` fetch (email + role), reads live presence from the realtime
 * socket, and offers quick **Message** / **Call** actions.
 *
 * Actions are gated the same way the rest of the app gates them: Message needs
 * chat-section access, Call needs `useCallsEnabled()` (the server/platform
 * matrix) — and BOTH are hidden when you're looking at yourself. If the fetch
 * hasn't resolved (or 404'd), the card still shows the name it was handed +
 * an initials avatar + presence, just without the email/role rows.
 */
export function UserProfileCard({
  userId,
  name,
  onClose,
}: {
  userId: number;
  name: string;
  onClose?: () => void;
}): JSX.Element {
  const profile = useUserProfile(userId).data;
  const { online } = useRealtime();
  const self = useSessionUser();
  const canChat = useCanSeeSection("chat");
  const callsEnabled = useCallsEnabled();
  const { ring } = useCallController();
  const navigate = useNavigate();

  const isSelf = self.id === userId;
  const displayName = profile?.name ?? name;

  const presence: PresenceState = useMemo(() => {
    const hit = online.find((e) => e.user_id === userId);
    return (hit?.status ?? "offline") as PresenceState;
  }, [online, userId]);
  const presenceLabel =
    presence === "offline" ? "Offline" : PRESENCE_STATUS_LABELS[presence as PresenceStatus];

  const handleMessage = (): void => {
    navigate(`/chat?dm=${userId}`);
    onClose?.();
  };
  const handleCall = (): void => {
    void ring({ user_id: userId, name: displayName });
    onClose?.();
  };

  return (
    <Box data-testid="user-profile-card" sx={{ p: 2, width: 288, maxWidth: "100%" }}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <UserAvatar name={displayName} userId={userId} size={56} presence={presence} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.25 }} noWrap>
            {displayName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <PresenceDot state={presence} size={8} />
            <Typography
              data-testid="user-profile-presence"
              sx={{ fontSize: 12.5, color: "text.secondary" }}
            >
              {presenceLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      {profile && (
        <Box sx={{ mt: 1.5, display: "grid", gap: 0.5 }}>
          <Typography
            data-testid="user-profile-role"
            sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}
          >
            {ROLE_LABELS[profile.dashboard_role]}
          </Typography>
          <Link
            data-testid="user-profile-email"
            href={`mailto:${profile.email}`}
            sx={{ fontSize: 13, color: "primary.main", wordBreak: "break-all" }}
          >
            {profile.email}
          </Link>
        </Box>
      )}

      {!isSelf && (canChat || callsEnabled) && (
        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
          {canChat && (
            <Button
              data-testid="user-profile-message"
              onClick={handleMessage}
              variant="contained"
              size="small"
              startIcon={<ChatRoundedIcon />}
              sx={{ flex: 1, textTransform: "none" }}
            >
              Message
            </Button>
          )}
          {callsEnabled && (
            <Button
              data-testid="user-profile-call"
              onClick={handleCall}
              variant="outlined"
              size="small"
              color="success"
              startIcon={<CallRoundedIcon />}
              sx={{ flex: 1, textTransform: "none" }}
            >
              Call
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
